import { Link } from "react-router-dom";
import logo from "@/assets/cart10-marketing-logo.png";

// =============================================================================
// The privacy policy and the data-deletion page.
//
// Both are PUBLIC — outside <Protected> in App.tsx — because Meta's reviewer
// and crawler fetch them without a session, and an app review fails on a
// policy URL that redirects to a login screen.
//
// Deliberately not run through react-i18next, unlike every other string in
// this app. These are statements of what we do with people's data, and a
// machine translation of a legal document is a liability: a mistranslated
// retention period or a softened disclosure is still binding. If these need to
// exist in another language, they need a human translator, not a t() call.
//
// Everything stated here is drawn from what the code actually does. If the
// data model, the processors or the deletion flow change, this changes with
// them — a policy that describes last quarter's product is worse than none.
// =============================================================================

/** Confirmed by the owner, 2026-10-13. It must stay a mailbox someone reads:
 *  Meta requires a working contact, and this is the address the privacy policy
 *  and the data-deletion page both give people for erasure requests. A dead
 *  address here is a failed app review at best and a broken promise at worst. */
const CONTACT_EMAIL = "hello@cart10.com";

const UPDATED = "13 October 2026";

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface text-ink">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Cart10 Marketing" className="h-7 w-auto" />
          </Link>
          <nav className="flex gap-4 text-sm">
            <Link to="/privacy" className="text-muted hover:text-ink">
              Privacy
            </Link>
            <Link to="/data-deletion" className="text-muted hover:text-ink">
              Delete your data
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-muted">Last updated {UPDATED}</p>
        <div className="mt-8 space-y-6 text-sm leading-relaxed">{children}</div>
      </main>

      <footer className="mx-auto max-w-3xl px-4 pb-12 text-xs text-muted">
        Cart10 Marketing · <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </footer>
    </div>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="pt-2 text-base font-semibold">{children}</h2>;
}

export function PrivacyPolicyPage() {
  return (
    <LegalShell title="Privacy policy">
      <p>
        Cart10 Marketing is a tool for small businesses to plan, write and publish
        social media posts. This page explains what we collect, why, who else sees it,
        and how to get rid of it. It describes what the product actually does rather
        than what we might one day do.
      </p>

      <H>What we collect</H>
      <p>When you create an account we store your name, email address and a hash of
      your password — never the password itself. We also store the language you chose
      for the interface.</p>
      <p>When you set up a brand we store its name, logo, industry, country and time
      zone, plus the brand voice you define: tone, audience, language and dialect
      notes, words to use or avoid, and any sample posts you paste in.</p>
      <p>When you connect a Facebook Page, Instagram account or Threads profile we
      store the account's name, its identifier on that platform, its avatar, and the
      access tokens the platform issues us. Those tokens are encrypted at rest with
      AES-256-GCM and are never written to our logs or sent to your browser.</p>
      <p>If you nominate someone to approve posts over WhatsApp, we store their name
      and phone number. That is someone else's personal data, so only add people who
      expect to hear from you, and remove them when they no longer should.</p>
      <p>We store the posts themselves — captions, hashtags, scheduled times, and
      once published, the post's identifier and permalink on the platform — along
      with the images you upload or we generate, and a record of how much you have
      generated and published so we can apply your plan's limits.</p>

      <H>Your images are stored in a public bucket</H>
      <p>
        This one deserves saying plainly. Instagram and Threads will not accept an
        image we hold privately: they fetch it themselves from a public URL. So every
        image you upload or generate is stored where anyone who has the URL can open
        it, without signing in. The URLs are long and not listed anywhere, but they
        are not secret. <strong>Do not put anything confidential in an image here.</strong>
      </p>

      <H>How we use it, and the AI involved</H>
      <p>To write your captions we send your brand voice, the topic, and a description
      of the image to Anthropic's Claude API. To create or improve images we send the
      image and a prompt to OpenAI. Both act as processors on our behalf for this
      purpose. We do not use your content to train our own models, and we have no
      models of our own to train.</p>
      <p>To publish, we send the caption and image to the platform you chose, using
      the token you granted. To send approval requests we use Meta's WhatsApp Business
      API.</p>

      <H>Who else sees it</H>
      <p>Only the services we need to run the product:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li><strong>Anthropic</strong> — caption generation</li>
        <li><strong>OpenAI</strong> — image generation and enhancement</li>
        <li><strong>Meta</strong> — publishing to Facebook, Instagram and Threads, and WhatsApp approvals</li>
        <li><strong>Cloudflare R2</strong> — image storage</li>
        <li><strong>Brevo</strong> — account emails such as verification and password resets</li>
        <li><strong>Paystack</strong> — payments. We never see or store your card details</li>
        <li><strong>Railway and Netlify</strong> — hosting the application and database</li>
      </ul>
      <p>We do not sell your data, and we do not share it for advertising.</p>

      <H>How long we keep it</H>
      <p>Your account data stays until you delete it. Images of published posts are
      removed from our bucket after their retention window passes, since the platform
      holds its own copy by then. Expired sessions and used email links are cleared
      automatically.</p>

      <H>Deleting your data</H>
      <p>
        You can delete your account yourself, from Account in the dashboard. It takes
        effect over 30 days and the detail is on the{" "}
        <Link to="/data-deletion" className="underline">
          data deletion page
        </Link>
        .
      </p>
      <p>
        One limit worth being clear about: posts we have already published are on
        Facebook, Instagram or Threads, under your own account. Deleting your Cart10
        Marketing account does not remove them, because they are not ours to remove.
        Delete those on the platform itself.
      </p>

      <H>Security</H>
      <p>Traffic is encrypted in transit. Social access tokens are encrypted at rest.
      Page tokens never reach the browser. Approvers' phone numbers are masked in our
      own interfaces. Passwords are stored as bcrypt hashes.</p>
      <p>We are a small team and this is an honest description of our measures, not a
      claim of perfection. If you find a problem, write to us — we would rather hear
      it from you.</p>

      <H>Your rights</H>
      <p>You can ask us what we hold about you, correct it, or have it deleted. Most
      of it you can see and change in the dashboard directly. For anything else, email{" "}
      <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.</p>

      <H>Children</H>
      <p>This is a business tool and not intended for anyone under 18.</p>

      <H>Changes</H>
      <p>If we change how we handle your data we will update this page and change the
      date at the top. If the change is significant we will email you.</p>

      <H>Contact</H>
      <p>
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
      </p>
    </LegalShell>
  );
}

export function DataDeletionPage() {
  return (
    <LegalShell title="Deleting your data">
      <p>
        You can delete your Cart10 Marketing account and everything in it yourself. No
        email, no waiting on us.
      </p>

      <H>How</H>
      <p>
        Sign in, open <strong>Account</strong> from your name at the bottom of the
        sidebar, and choose <strong>Delete my account</strong>. You will be asked for
        your password to confirm.
      </p>
      <p>
        If you cannot sign in, email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>{" "}
        from the address on the account and we will do it for you.
      </p>

      <H>What happens straight away</H>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Every connected Facebook Page, Instagram account and Threads profile is
          disconnected, and the access tokens we held are destroyed. We stop being able
          to post as you from that moment.
        </li>
        <li>Nothing further is published, including anything already scheduled.</li>
        <li>Your account is marked for deletion in 30 days.</li>
      </ul>

      <H>What happens after 30 days</H>
      <p>The following is permanently deleted and cannot be recovered:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Your user account, name and email address</li>
        <li>Your workspaces, brands and brand voices</li>
        <li>Every post, draft, caption and campaign</li>
        <li>Your approvers' names and phone numbers</li>
        <li>Every image you uploaded or we generated, deleted from storage</li>
        <li>Your usage history and credit ledger</li>
      </ul>

      <H>Why 30 days</H>
      <p>
        So that a misclick is survivable. For those 30 days you can still sign in, and
        the Account page will offer to cancel the deletion.
      </p>
      <p>
        Cancelling restores your account but <strong>not</strong> your connected pages.
        Those tokens were destroyed the moment you asked, and only Facebook can issue
        new ones — so you would need to reconnect your pages.
      </p>

      <H>What we cannot delete</H>
      <p>
        Posts that were already published are on Facebook, Instagram or Threads, in
        your own account. They stay there. We have no way to retract them and, once
        your tokens are destroyed, no access to try. Delete those on the platform.
      </p>
      <p>
        Payment records held by Paystack are subject to their retention rules and to
        the financial record-keeping we are required to do.
      </p>

      <H>Questions</H>
      <p>
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
      </p>
    </LegalShell>
  );
}
